import Box from '@mui/material/Box';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { parse } from 'date-fns';
import { es } from 'date-fns/locale';
import React, { useEffect, useMemo, useState } from 'react';

import { datePickerSlotProps } from '@/utils/datePickerConfig';

type PopperSlotProps = NonNullable<DatePickerProps['slotProps']>['popper'];

type RangeDatePickerProps = {
  startLabel?: string;
  endLabel?: string;
  onChange?: (filterName: string, value: string) => void;
  untilToday?: boolean;
  startValue?: string;
  endValue?: string;
  noPadding?: boolean;
  disablePortal?: boolean;
  direction?: 'row' | 'column';
};

const RangeDatePicker: React.FC<RangeDatePickerProps> = ({
  startLabel = 'Start Date',
  endLabel = 'End Date',
  onChange,
  untilToday,
  startValue = '',
  endValue = '',
  noPadding = false,
  disablePortal = true,
  direction,
}) => {
  const [startDate, setStartDate] = useState<string | null>(startValue || null);
  const [endDate, setEndDate] = useState<string | null>(endValue || null);

  useEffect(() => {
    setStartDate(startValue || null);
  }, [startValue]);

  useEffect(() => {
    setEndDate(endValue || null);
  }, [endValue]);

  const formatDate = (date: Date | null) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const cleanStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parsed = parse(cleanStr, 'yyyy-MM-dd', new Date());
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleDateChange = (type: 'startDate' | 'endDate') => (date: Date | null) => {
    const formattedDate = formatDate(date);
    if (type === 'startDate') setStartDate(formattedDate);
    else setEndDate(formattedDate);

    onChange?.(type, formattedDate ?? '');
  };

  const commonProps = useMemo(
    () => ({
      slotProps: {
        ...datePickerSlotProps,
        textField: {
          size: 'small' as const,
        },
        popper: {
          ...datePickerSlotProps?.popper,
          disablePortal,
        } as PopperSlotProps,
      },
      sx: { width: '100%' },
    }),
    [disablePortal],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Box
        display="flex"
        gap={2}
        flexDirection={direction === 'column' ? 'column' : { xs: 'column', sm: 'row' }}
        sx={{
          paddingBottom: noPadding ? 0 : '1rem',
          width: '100%',
        }}
      >
        <DatePicker
          {...commonProps}
          label={startLabel}
          value={parseDate(startDate)}
          onChange={handleDateChange('startDate')}
          maxDate={untilToday ? new Date() : (parseDate(endDate) ?? undefined)}
        />
        <DatePicker
          {...commonProps}
          label={endLabel}
          value={parseDate(endDate)}
          onChange={handleDateChange('endDate')}
          minDate={parseDate(startDate) ?? undefined}
          maxDate={untilToday ? new Date() : undefined}
          disabled={!startDate}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default RangeDatePicker;
